import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AlertTriangle, CheckCircle2, RefreshCw, ShieldCheck } from 'lucide-react'
import { ContentHeader } from '../../components/layout/ContentHeader'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { Button } from '../../components/ui/Button'
import { usePool, type PoolAccount } from '../../hooks/usePool'
import { describeScopes } from '../../lib/google-scopes'
import { redirectTo } from '../../lib/navigation'

export default function GoogleAccount() {
  const { accounts, loading, error: poolError, refetch } = usePool()
  const [searchParams] = useSearchParams()

  const [reconnectEmail, setReconnectEmail] = useState<string | null>(null)
  const [reconnecting, setReconnecting] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  const reconnected = searchParams.get('reconnected') === '1'
  const errorParam = searchParams.get('error')

  async function handleConfirmReconnect() {
    if (!reconnectEmail) return
    setReconnecting(true)
    setActionError(null)
    try {
      const res = await fetch(
        `/api/data-sources/pool/reconnect-url?email=${encodeURIComponent(reconnectEmail)}`,
        { credentials: 'include' },
      )
      const json = await res.json().catch(() => ({}))

      if (!res.ok || json?.success === false || !json?.authUrl) {
        throw new Error(json?.error || 'La reconnexion a échoué. Merci de réessayer.')
      }

      setReconnectEmail(null)
      redirectTo(json.authUrl as string)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'La reconnexion a échoué.')
      setReconnectEmail(null)
      setReconnecting(false)
    }
  }

  return (
    <div className="p-6 max-w-3xl">
      <ContentHeader
        breadcrumb={[{ label: 'Paramètres' }, { label: 'Connexion Google' }]}
        title="Connexion Google"
        subtitle="Comptes Google connectés pour la connexion et les intégrations (Business Profile, Analytics, Search Console)."
      />

      {reconnected && (
        <div
          role="status"
          className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800"
        >
          Compte reconnecté avec succès.
        </div>
      )}

      {errorParam && (
        <div
          role="alert"
          className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800"
        >
          {errorParam}
        </div>
      )}

      {loading && (
        <div className="rounded-2xl border border-[#E2E4ED] bg-white p-6">
          <p className="text-sm text-[#8B8FA7]" aria-live="polite">
            Chargement des comptes Google…
          </p>
        </div>
      )}

      {!loading && poolError && (
        <div
          role="alert"
          className="rounded-2xl border border-red-200 bg-red-50 p-6 text-sm text-red-800"
        >
          <p className="font-semibold">Impossible de charger les comptes Google</p>
          <p className="mt-1">{poolError}</p>
          <Button variant="secondary" icon={RefreshCw} onClick={refetch} className="mt-4">
            Réessayer
          </Button>
        </div>
      )}

      {!loading && !poolError && accounts.length === 0 && (
        <div className="rounded-2xl border border-[#E2E4ED] bg-white p-6">
          <p className="text-sm text-[#8B8FA7]">
            Aucun compte Google connecté. Connectez une source depuis un projet.
          </p>
        </div>
      )}

      {!loading && !poolError && accounts.length > 0 && (
        <div className="space-y-6">
          {actionError && (
            <div
              role="alert"
              className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800"
            >
              <p className="font-semibold">La reconnexion n’a pas abouti</p>
              <p className="mt-1">{actionError}</p>
            </div>
          )}

          {accounts.map((account) => (
            <AccountCard
              key={account.email}
              account={account}
              onReconnect={() => {
                setActionError(null)
                setReconnectEmail(account.email)
              }}
              reconnecting={reconnecting && reconnectEmail === account.email}
            />
          ))}
        </div>
      )}

      {reconnectEmail && (
        <ConfirmDialog
          title="Confirmer la reconnexion Google"
          confirmLabel="Confirmer la reconnexion"
          cancelLabel="Annuler"
          isLoading={reconnecting}
          onConfirm={handleConfirmReconnect}
          onCancel={() => setReconnectEmail(null)}
        >
          <p>
            La reconnexion révoque immédiatement les accès Google de ce compte, puis redirige vers
            l’écran de consentement Google.
          </p>
          <p>
            Si la procédure est interrompue avant la fin, les intégrations Google concernées
            resteront hors service jusqu’à ce que la reconnexion soit menée à son terme.
          </p>
        </ConfirmDialog>
      )}
    </div>
  )
}

function AccountCard({
  account,
  onReconnect,
  reconnecting,
}: {
  account: PoolAccount
  onReconnect: () => void
  reconnecting: boolean
}) {
  const grantedScopes = describeScopes(account.scopes)
  const healthy = !account.reauthNeeded

  return (
    <div className="rounded-2xl border border-[#E2E4ED] bg-white p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-sm font-semibold text-[#1B2559]">{account.email}</h2>
          {healthy ? (
            <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
              <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.75} />
              Actif
            </span>
          ) : (
            <span
              role="alert"
              className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800"
            >
              <AlertTriangle className="w-3.5 h-3.5" strokeWidth={1.75} />
              Reconnexion requise
            </span>
          )}
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-[#8B8FA7]" strokeWidth={1.5} />
        <h3 className="text-xs font-semibold uppercase tracking-wide text-[#8B8FA7]">
          Autorisations accordées
        </h3>
      </div>

      {grantedScopes.length === 0 ? (
        <p className="mt-3 text-sm text-[#8B8FA7]">
          Aucune autorisation Google active pour ce compte.
        </p>
      ) : (
        <ul className="mt-3 space-y-3">
          {grantedScopes.map((s) => (
            <li key={s.scope} className="flex items-start gap-3">
              <CheckCircle2
                className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5"
                strokeWidth={1.75}
              />
              <div>
                <p className="text-sm font-medium text-[#1B2559]">{s.label}</p>
                <p className="text-xs text-[#8B8FA7]">{s.description}</p>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-[#E2E4ED] pt-5">
        <Button
          icon={RefreshCw}
          variant={healthy ? 'secondary' : 'primary'}
          onClick={onReconnect}
          isLoading={reconnecting}
        >
          Reconnecter
        </Button>
        <p className="text-xs text-[#8B8FA7]">
          La reconnexion révoque les accès Google actuels et redirige vers Google.
        </p>
      </div>
    </div>
  )
}
